/**
 * My Team — org hierarchy of reports with usage/ROI summaries.
 *
 * Two views over the same data:
 *  - Table: expandable tree, several levels at once.
 *  - Org chart: Workday-style single focus. One person is the head node and their
 *    direct reports sit below; drilling into a report re-roots the chart on them,
 *    so only one level is ever on screen and the layout never overflows.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUp,
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  Home,
  Network,
  RefreshCw,
  Search,
  Table as TableIcon,
  Users,
  X,
} from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import TeamMemberDetailModal from '../modals/TeamMemberDetailModal';
import GarageLoadingScreen, { GarageLoadingRegion } from './GarageLoadingScreen';
import './GarageMyTeamSection.css';

const WINDOW_LABELS = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  all: 'All time',
};

const PRESET_RANGES = ['7d', '30d', '90d', 'all'];

// Stable per-person accent so the same face keeps its colour as you traverse.
const AVATAR_COLORS = [
  '#4ecdc4',
  '#5b8def',
  '#a78bfa',
  '#f472b6',
  '#f59e0b',
  '#10b981',
  '#0ea5e9',
  '#f97316',
];

const colorFor = (key) => {
  const s = String(key || '');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const formatWindowLabel = (range, startDate, endDate) => {
  if (range === 'custom' && startDate && endDate) {
    const fmt = (d) =>
      new Date(`${d}T00:00:00`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    return `${fmt(startDate)} – ${fmt(endDate)}`;
  }
  return WINDOW_LABELS[range] || WINDOW_LABELS['90d'];
};

const formatMinutes = (minutes) => {
  if (minutes == null || Number.isNaN(Number(minutes))) return '—';
  const m = Number(minutes);
  if (m <= 0) return '0m';
  const hrs = m / 60;
  if (hrs >= 10) return `${Math.round(m).toLocaleString()}m (${hrs.toFixed(1)}h)`;
  return `${Math.round(m).toLocaleString()}m`;
};

const getInitials = (nameOrEmail) => {
  const parts = String(nameOrEmail || '')
    .replace(/\(.*?\)/g, '')
    .split(/[\s@._-]+/)
    .filter(Boolean);
  if (parts.length === 0) return 'U';
  return parts
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');
};

const GarageMyTeamSection = ({ userName = '', canBrowseOrg = false, refreshKey = 0 }) => {
  const [dateRange, setDateRange] = useState('90d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [stagingStartDate, setStagingStartDate] = useState('');
  const [stagingEndDate, setStagingEndDate] = useState('');
  const datePickerRef = useRef(null);

  const [viewMode, setViewMode] = useState('table');

  // Navigation path below the viewer's own team. Empty = "my team"; the last
  // entry is the person the chart is currently focused on.
  const [trail, setTrail] = useState([]);
  // Keep the rendered path stable until the requested level arrives. This lets
  // the current graph remain visible while traversal/filter requests run.
  const [displayTrail, setDisplayTrail] = useState([]);

  const [context, setContext] = useState(null);
  const [roots, setRoots] = useState([]);
  const [rootManager, setRootManager] = useState(null);
  const [childrenByEmail, setChildrenByEmail] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loadingRoot, setLoadingRoot] = useState(true);
  const [loadingChildren, setLoadingChildren] = useState({});
  const [error, setError] = useState(null);
  const rootCacheRef = useRef(new Map());
  const rootRequestRef = useRef(0);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const focusEmail = trail.length ? trail[trail.length - 1].email : null;

  const windowLabel = useMemo(
    () => formatWindowLabel(dateRange, customStartDate, customEndDate),
    [dateRange, customStartDate, customEndDate]
  );

  const windowParams = useMemo(() => {
    const params = { window: dateRange };
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      params.start_date = customStartDate;
      params.end_date = customEndDate;
    }
    return params;
  }, [dateRange, customStartDate, customEndDate]);

  const customIncomplete =
    dateRange === 'custom' && (!customStartDate || !customEndDate);

  const loadRoot = useCallback(async () => {
    if (customIncomplete) return;
    const requestId = rootRequestRef.current + 1;
    rootRequestRef.current = requestId;
    const cacheKey = JSON.stringify({
      ...windowParams,
      manager_email: focusEmail || '',
    });
    const cached = rootCacheRef.current.get(cacheKey);
    if (cached) {
      setRoots(cached.members);
      setRootManager(cached.manager);
      setDisplayTrail(trail);
    }
    setLoadingRoot(true);
    setError(null);
    try {
      const ctx = await garageAPI.getMyTeamContext();
      if (requestId !== rootRequestRef.current) return;
      setContext(ctx);
      if (!ctx?.configured) {
        setRoots([]);
        setError('Workday org database is not configured on the server.');
        return;
      }
      // Admins without reports start empty and pick a manager via search
      if (!ctx?.hasDirectReports && !focusEmail) {
        setRoots([]);
        setRootManager(null);
        setDisplayTrail([]);
        return;
      }
      const data = await garageAPI.getTeamChildren({
        ...windowParams,
        ...(focusEmail ? { manager_email: focusEmail } : {}),
      });
      if (requestId !== rootRequestRef.current) return;
      const members = data?.members || [];
      const manager = data?.manager || null;
      rootCacheRef.current.set(cacheKey, { members, manager });
      setRoots(members);
      setRootManager(manager);
      setDisplayTrail(trail);
      setChildrenByEmail({});
      setExpanded({});
    } catch (err) {
      if (requestId !== rootRequestRef.current) return;
      console.error('Failed to load My Team', err);
      setError(
        err.response?.data?.detail || err.message || 'Failed to load My Team'
      );
    } finally {
      if (requestId === rootRequestRef.current) setLoadingRoot(false);
    }
  }, [windowParams, focusEmail, customIncomplete, trail]);

  useEffect(() => {
    // refreshKey changes when the page-level Refresh button is clicked.
    if (refreshKey >= 0) loadRoot();
  }, [loadRoot, refreshKey]);

  useEffect(() => {
    if (!showCustomPicker) return undefined;
    const onDocClick = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowCustomPicker(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showCustomPicker]);

  useEffect(() => {
    if (!canBrowseOrg) return undefined;
    const q = searchQuery.trim();
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        setSearchResults(await garageAPI.searchOrgPeople(q, 15));
      } catch (err) {
        console.error('Org search failed', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery, canBrowseOrg]);

  const applyPresetRange = (range) => {
    setDateRange(range);
    setCustomStartDate('');
    setCustomEndDate('');
    setShowCustomPicker(false);
  };

  const applyCustomRange = () => {
    if (!stagingStartDate || !stagingEndDate) return;
    let start = stagingStartDate;
    let end = stagingEndDate;
    if (end < start) {
      [start, end] = [end, start];
    }
    setCustomStartDate(start);
    setCustomEndDate(end);
    setDateRange('custom');
    setShowCustomPicker(false);
  };

  const drillInto = (person) => {
    if (!person?.email || loadingRoot) return;
    setTrail((t) => [...t, person]);
  };

  const jumpToTrail = (index) => {
    if (loadingRoot) return;
    setTrail(displayTrail.slice(0, index + 1));
  };

  const goHome = () => {
    if (!loadingRoot) setTrail([]);
  };

  // One level up is wherever we came from. At the top of our own path only a
  // dashboard admin may climb past themselves, which re-roots the trail.
  const goUp = () => {
    if (loadingRoot) return;
    if (displayTrail.length > 0) {
      setTrail(displayTrail.slice(0, -1));
      return;
    }
    const managerEmail = rootManager?.managerEmail || context?.self?.managerEmail;
    const managerName = rootManager?.managerName || context?.self?.managerName;
    if (canBrowseOrg && managerEmail) {
      setTrail([
        { email: managerEmail, name: managerName || managerEmail, origin: 'ancestor' },
      ]);
    }
  };

  const startAt = (person) => {
    if (loadingRoot) return;
    setTrail([{ ...person, origin: 'search' }]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const toggleExpand = async (member) => {
    const email = (member.email || '').toLowerCase();
    if (!member.hasChildren || !email) return;

    const nextOpen = !expanded[email];
    setExpanded((prev) => ({ ...prev, [email]: nextOpen }));
    if (!nextOpen || childrenByEmail[email]) return;

    setLoadingChildren((prev) => ({ ...prev, [email]: true }));
    try {
      const data = await garageAPI.getTeamChildren({
        ...windowParams,
        manager_email: member.email,
      });
      setChildrenByEmail((prev) => ({
        ...prev,
        [email]: data?.members || [],
      }));
    } catch (err) {
      console.error('Failed to load reports', err);
      setChildrenByEmail((prev) => ({ ...prev, [email]: [] }));
    } finally {
      setLoadingChildren((prev) => ({ ...prev, [email]: false }));
    }
  };

  const openMember = async (member) => {
    if (!member?.email) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const data = await garageAPI.getTeamMemberDetail(member.email, windowParams);
      setDetail(data);
    } catch (err) {
      console.error('Failed to load member detail', err);
      setDetail({
        person: {
          email: member.email,
          name: member.name,
          title: member.title,
        },
        summary: {
          sessions: member.sessions || 0,
          toolsAdopted: member.toolsAdopted || 0,
          timeSavedMin: member.timeSavedMin || 0,
        },
        roiTools: [],
        usageTools: [],
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const renderRows = (members, depth = 0) =>
    (members || []).map((m) => {
      const key = (m.email || m.employeeId || '').toLowerCase();
      const isOpen = !!expanded[key];
      const kids = childrenByEmail[key] || [];
      const kidsLoading = !!loadingChildren[key];

      return (
        <React.Fragment key={key || m.name}>
          <tr className="my-team-row">
            <td>
              <div
                className="my-team-name-cell"
                style={{ paddingLeft: `${depth * 18}px` }}
              >
                {m.hasChildren ? (
                  <button
                    type="button"
                    className="my-team-expand-btn"
                    onClick={() => toggleExpand(m)}
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                  >
                    {kidsLoading ? (
                      <RefreshCw size={14} className="spin" />
                    ) : isOpen ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                  </button>
                ) : (
                  <span className="my-team-expand-spacer" />
                )}
                <button
                  type="button"
                  className="my-team-person-btn"
                  onClick={() => openMember(m)}
                >
                  <span className="my-team-person-name">{m.name || m.email}</span>
                  {m.title ? (
                    <span className="my-team-person-title">{m.title}</span>
                  ) : null}
                </button>
              </div>
            </td>
            <td className="my-team-muted">{m.email}</td>
            <td className="my-team-num">{m.sessions ?? 0}</td>
            <td className="my-team-num">{m.toolsAdopted ?? 0}</td>
            <td className="my-team-num">{formatMinutes(m.timeSavedMin)}</td>
            <td className="my-team-num">
              {m.hasChildren ? m.directReportCount || '—' : '—'}
            </td>
          </tr>
          {isOpen && kidsLoading && (
            <tr>
              <td colSpan={6} className="my-team-nested-loading">
                <GarageLoadingScreen compact message="Loading direct reports…" />
              </td>
            </tr>
          )}
          {isOpen && !kidsLoading && renderRows(kids, depth + 1)}
        </React.Fragment>
      );
    });

  const totalSessions = roots.reduce((s, m) => s + (m.sessions || 0), 0);
  const totalSaved = roots.reduce((s, m) => s + (m.timeSavedMin || 0), 0);
  const isBrowsingOther = displayTrail.length > 0;
  const rootLabel = isBrowsingOther
    ? rootManager?.name || focusEmail
    : userName || context?.self?.name || 'You';

  const focusPerson = rootManager || {
    email: context?.self?.email,
    name: rootLabel,
    title: context?.self?.title,
    managerEmail: context?.self?.managerEmail,
    managerName: context?.self?.managerName,
  };

  // A trail opened from search — or from climbing above ourselves — is not below
  // the viewer, so it must not be drawn hanging off their own name.
  const trailIsBelowMe = (displayTrail[0]?.origin || 'mine') === 'mine';
  const homeLabel = trailIsBelowMe ? userName || 'My team' : 'My org';
  const atForeignRoot =
    isBrowsingOther && displayTrail.length === 1 && !trailIsBelowMe;

  const upLabel = isBrowsingOther
    ? displayTrail.length > 1
      ? displayTrail[displayTrail.length - 2].name
      : userName || 'my team'
    : focusPerson.managerName || '';
  const canGoUp =
    isBrowsingOther || (canBrowseOrg && Boolean(focusPerson.managerEmail));

  const showEmptyNoReports =
    !context?.hasDirectReports && !isBrowsingOther && !error;

  const renderBreadcrumbs = () => (
    <div className="my-team-crumbs">
      <div className="my-team-crumb-trail" aria-label="Org path">
        <button
          type="button"
          className="my-team-crumb"
          onClick={goHome}
          disabled={!isBrowsingOther || loadingRoot}
        >
          <Home size={12} />
          {homeLabel}
        </button>
        {displayTrail.map((p, i) => {
          const isCurrent = i === displayTrail.length - 1;
          return (
            <React.Fragment key={p.email || i}>
              <ChevronRight size={12} className="my-team-crumb-sep" />
              <button
                type="button"
                className={`my-team-crumb ${isCurrent ? 'current' : ''}`}
                onClick={() => jumpToTrail(i)}
                disabled={isCurrent || loadingRoot}
                aria-current={isCurrent ? 'page' : undefined}
              >
                {p.name || p.email}
              </button>
            </React.Fragment>
          );
        })}
      </div>
      {canGoUp && (
        <button
          type="button"
          className="my-team-up-btn"
          onClick={goUp}
          disabled={loadingRoot}
          title={
            atForeignRoot
              ? 'Back to My Org'
              : upLabel
                ? `Up to ${upLabel}`
                : 'Up one level'
          }
        >
          <ArrowUp size={12} />
          {atForeignRoot ? 'Back to My Org' : `Up${upLabel ? ` to ${upLabel}` : ''}`}
        </button>
      )}
    </div>
  );

  const renderReportCard = (m) => {
    const name = m.name || m.email;
    const title = m.title || '';
    return (
      <div className="my-team-node" key={(m.email || m.employeeId || name).toLowerCase()}>
        <button
          type="button"
          className="my-team-node-card"
          onClick={() => openMember(m)}
          title={`${name}${title ? ` · ${title}` : ''} — open Usage and Time Saved`}
        >
          <span
            className="my-team-node-avatar"
            style={{ background: colorFor(m.email || name) }}
          >
            {getInitials(name)}
          </span>
          <span className="my-team-node-body">
            <span className="my-team-node-name">{name}</span>
            <span className="my-team-node-title">{title || '—'}</span>
            <span className="my-team-node-stats">
              <span className="my-team-node-chip">
                {(m.sessions ?? 0).toLocaleString()} sessions
              </span>
              <span className="my-team-node-chip saved">
                {formatMinutes(m.timeSavedMin)}
              </span>
            </span>
          </span>
        </button>
        {m.hasChildren ? (
          <button
            type="button"
            className="my-team-node-drill"
            onClick={() => drillInto(m)}
            disabled={loadingRoot}
          >
            <Users size={11} />
            {m.directReportCount} report{m.directReportCount === 1 ? '' : 's'}
            <ChevronRight size={11} />
          </button>
        ) : (
          <span className="my-team-node-drill placeholder">Individual contributor</span>
        )}
      </div>
    );
  };

  return (
    <div className="my-team-section">
      <div className="my-team-hero">
        <div className="my-team-hero-main">
          <div className="my-team-hero-label">
            <Network size={14} />
            <span>{isBrowsingOther ? 'Org view' : 'My Team'} · {rootLabel}</span>
          </div>
          <p className="my-team-hero-copy">
            Click a person for Usage and Time Saved. Open their reports to move down the org.
          </p>
          {canBrowseOrg && (
            <div className="my-team-search">
              <div className="my-team-search-input-wrap">
                <Search size={15} className="my-team-search-icon" />
                <input
                  type="text"
                  className="my-team-search-input"
                  placeholder="Search anyone to view their org…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="my-team-search-clear"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
                {(searching || searchResults.length > 0) && (
                  <div className="my-team-search-results">
                    {searching ? (
                      <div className="my-team-search-empty">Searching…</div>
                    ) : (
                      searchResults.map((p) => (
                        <button
                          key={p.email || p.employeeId}
                          type="button"
                          className="my-team-search-result"
                          onClick={() => startAt(p)}
                        >
                          <span className="my-team-search-result-name">{p.name}</span>
                          <span className="my-team-search-result-meta">
                            {p.title || p.email}
                            {p.directReportCount
                              ? ` · ${p.directReportCount} report${p.directReportCount === 1 ? '' : 's'}`
                              : ' · no reports'}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="my-team-hero-metrics">
          <div className="my-team-metric-card">
            <div className="my-team-metric-label">Direct reports</div>
            <div className="my-team-metric-value">
              {isBrowsingOther
                ? roots.length
                : context?.directReportCount ?? roots.length}
            </div>
          </div>
          <div className="my-team-metric-card">
            <div className="my-team-metric-label">Team sessions</div>
            <div className="my-team-metric-value">{totalSessions.toLocaleString()}</div>
          </div>
          <div className="my-team-metric-card">
            <div className="my-team-metric-label">
              <Clock size={12} style={{ display: 'inline', marginRight: 4 }} />
              Team time saved
            </div>
            <div className="my-team-metric-value">{formatMinutes(totalSaved)}</div>
          </div>
        </div>
      </div>

      <div className="my-team-toolbar">
        <div className="my-team-window-seg" role="group" aria-label="Time window">
          {PRESET_RANGES.map((range) => (
            <button
              key={range}
              type="button"
              className={`my-team-window-btn ${dateRange === range ? 'active' : ''}`}
              onClick={() => applyPresetRange(range)}
            >
              {WINDOW_LABELS[range]}
            </button>
          ))}
          <div className="my-team-custom-wrap" ref={datePickerRef}>
            <button
              type="button"
              className={`my-team-window-btn ${dateRange === 'custom' ? 'active' : ''}`}
              onClick={() => {
                setStagingStartDate(customStartDate || '');
                setStagingEndDate(customEndDate || '');
                setShowCustomPicker((v) => !v);
              }}
            >
              <Calendar size={13} />
              {dateRange === 'custom' && customStartDate && customEndDate
                ? windowLabel
                : 'Custom'}
            </button>
            {showCustomPicker && (
              <div className="my-team-custom-panel" onClick={(e) => e.stopPropagation()}>
                <label>
                  Start
                  <input
                    type="date"
                    value={stagingStartDate}
                    onChange={(e) => setStagingStartDate(e.target.value)}
                  />
                </label>
                <label>
                  End
                  <input
                    type="date"
                    value={stagingEndDate}
                    onChange={(e) => setStagingEndDate(e.target.value)}
                  />
                </label>
                <div className="my-team-custom-actions">
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => {
                      setShowCustomPicker(false);
                      if (dateRange === 'custom') applyPresetRange('90d');
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="primary"
                    disabled={!stagingStartDate || !stagingEndDate}
                    onClick={applyCustomRange}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="my-team-view-seg" role="group" aria-label="View type">
          <button
            type="button"
            className={`my-team-view-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            <TableIcon size={14} />
            Table
          </button>
          <button
            type="button"
            className={`my-team-view-btn ${viewMode === 'chart' ? 'active' : ''}`}
            onClick={() => setViewMode('chart')}
          >
            <Network size={14} />
            Org chart
          </button>
        </div>
      </div>

      {error && (
        <div className="my-team-error" role="alert">
          {error}
        </div>
      )}

      <GarageLoadingRegion loading={loadingRoot && roots.length > 0} message="Updating your team…">
      {customIncomplete ? (
        <div className="my-team-empty">
          <Calendar size={28} />
          <p>Pick a start and end date to apply the custom range.</p>
        </div>
      ) : loadingRoot && roots.length === 0 ? (
        <GarageLoadingScreen message="Loading direct reports…" />
      ) : showEmptyNoReports ? (
        <div className="my-team-empty">
          <Users size={28} />
          <p>
            {canBrowseOrg
              ? 'You have no direct reports in Workday. Search for anyone above to view their org.'
              : 'No direct reports found in Workday for your account.'}
          </p>
        </div>
      ) : roots.length === 0 && !error ? (
        <div className="my-team-empty">
          <Users size={28} />
          <p>No team members to show.</p>
          {isBrowsingOther && (
            <button type="button" className="my-team-up-btn" onClick={goUp}>
              <ArrowUp size={12} />
              Up{upLabel ? ` to ${upLabel}` : ''}
            </button>
          )}
        </div>
      ) : viewMode === 'chart' ? (
        <div
          className="my-team-org"
          aria-busy={loadingRoot}
        >
          {renderBreadcrumbs()}
          <div className="my-team-stage">
            <div className="my-team-head">
              <button
                type="button"
                className="my-team-head-card"
                onClick={() => openMember(focusPerson)}
                title={`${focusPerson.name} — open Usage and Time Saved`}
              >
                <span className="my-team-head-avatar">
                  {getInitials(focusPerson.name || focusPerson.email)}
                </span>
                <span className="my-team-head-body">
                  <span className="my-team-head-name">
                    {focusPerson.name || focusPerson.email}
                  </span>
                  <span className="my-team-head-title">
                    {focusPerson.title || '—'}
                  </span>
                  <span className="my-team-head-stats">
                    <span className="my-team-node-chip">
                      {roots.length} direct report{roots.length === 1 ? '' : 's'}
                    </span>
                    <span className="my-team-node-chip">
                      {totalSessions.toLocaleString()} team sessions
                    </span>
                    <span className="my-team-node-chip saved">
                      {formatMinutes(totalSaved)} saved
                    </span>
                  </span>
                </span>
              </button>
            </div>

            <div className="my-team-level-label">
              <span className="my-team-level-line" />
              <span className="my-team-level-text">
                Direct reports · {roots.length}
              </span>
              <span className="my-team-level-line" />
            </div>

            <div className="my-team-level">{roots.map(renderReportCard)}</div>
          </div>
        </div>
      ) : (
        <>
          {isBrowsingOther && renderBreadcrumbs()}
          <div
            className="my-team-table-wrap"
            aria-busy={loadingRoot}
          >
            <table className="my-team-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Email</th>
                  <th>Sessions</th>
                  <th>Tools</th>
                  <th>Time saved</th>
                  <th>Reports</th>
                </tr>
              </thead>
              <tbody>{renderRows(roots)}</tbody>
            </table>
          </div>
        </>
      )}

      </GarageLoadingRegion>

      <TeamMemberDetailModal
        isOpen={detailOpen}
        detail={detail}
        loading={detailLoading}
        windowLabel={windowLabel}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
};

export default GarageMyTeamSection;
